import { History } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { t } from 'i18next';
import { useContext, useState } from 'react';
import DatagridWrapper, { DataGridId } from '../../components/DatagridWrapper';
import InfoButton from '../../components/InfoButton';
import InfoDrawer from '../../components/InfoDrawer';
import { OrganisationContext } from '../../components/Layout/OrganisationPicker';
import { OpenInNewTabButton } from '../../components/OpenInNewTabButton';
import { PaginationQueryPackage } from '../../models/API/QueryParams/PaginationQueryPackage';
import { GetManyPackage } from '../../redux/GetManyPackage';
import { NotCorrectRights } from '../Permission/NotCorrectRights';
import { PermissionEnum } from '../Permission/PermissionEnum';
import { AbilityContext, Can } from '../UserApi/logic/Can';
import { RecordInfoDialog } from './dnsInfoDialog/DnsInfoDialog';
import { DnsCreateTXTResponse } from '../../models/dns';



class TXTRecordListProps {
    getRecords!: UseQuery<QueryDefinition<GetManyPackage, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>, any, DnsCreateTXTResponse[], any>>
    maxSelected?: number
    checkedAuthorisations?: DnsCreateTXTResponse[]
    idToFetch?: string
    simpleView?: boolean = false;
    dataGridId?: DataGridId
}

export const TXTRecordList = (props: TXTRecordListProps) => {
    const ability = useContext(AbilityContext);
    const organisationContext = useContext(OrganisationContext)?.organisation;


    const canReadList = ability.can(PermissionEnum.AUTHORISATION_READ, organisationContext)
    if (!canReadList) return <NotCorrectRights />

    return (<TXTListContent {...props} />)
}

export function TXTListContent(props: TXTRecordListProps) {
    const currentResult = props.getRecords({
        pagination: new PaginationQueryPackage(),
        uuid: props.idToFetch!,
    });


    const [clickedRow, setClickedRow] = useState<DnsCreateTXTResponse | undefined>(undefined)

    if (currentResult.data === undefined)
        currentResult.data = []

    const getColumns = (records: DnsCreateTXTResponse[]) => {
        const simpleView: GridColDef[] = [
            //Data
            { field: "uuid", sortable: true, type: "string", headerName: t('ID'), flex: 1 },
            { field: "fqdn", sortable: true, type: "string", headerName: t('FQDN'), flex: 1 },
            { field: "value", sortable: true, type: "string", headerName: t('Value'), flex: 1 },
            { field: "ttl", sortable: true, type: "string", headerName: t('TTL'), flex: 1 },

        ];

        const advancedView: GridColDef[] = [
        ];

        const buttonsView: GridColDef[] = [
            {
                field: 'events',
                sortable: false,
                hideable: false,
                type: "string",
                valueGetter: () => "",
                headerName: t('Actions'),
                minWidth: props.simpleView ? 100 : 220,
                renderCell: (params: any) => {
                    const record = records.find(x => x.uuid === params.id);
                    return (props.simpleView ?
                        <OpenInNewTabButton url={`/records/${record?.uuid}`} />
                        :
                        <>
                            <Can I={PermissionEnum.DNS_RECORD_READ} this={record}>
                                <InfoButton onClick={() => setClickedRow(record)} />
                            </Can>
                            <Can I={PermissionEnum.AUDIT_READ} this={record}>
                                <OpenInNewTabButton tooltipText='Events' icon={<History />} url={`/events?entity_uuid=${record?.uuid}`} />
                            </Can>
                            <Can I={PermissionEnum.DNS_RECORD_READ} this={record}>
                                <OpenInNewTabButton url={`/records/${record?.uuid}`} />
                            </Can>
                        </>
                    )
                }
            }
        ];

        const simpleList = simpleView;
        const advancedList = advancedView;
        const listToShow = props.simpleView ? simpleList : simpleList.concat(advancedList);
        return listToShow.concat(buttonsView);
    }

    return (
        <>
            <DatagridWrapper
                dataGridId={props.dataGridId ?? DataGridId.RECORDS}
                onRowClick={(row) => { setClickedRow(row.row) }}
                loading={currentResult.isLoading}
                rows={currentResult.data}
                columns={getColumns(currentResult.data)}
            />

            <InfoDrawer
                open={clickedRow != undefined}
                onClose={() => setClickedRow(undefined)}
                entityUuid={clickedRow?.uuid}>
                {clickedRow &&
                    <RecordInfoDialog
                        key={clickedRow.uuid}
                        recordUuid={clickedRow.uuid}
                        afterDeleteAction={() => setClickedRow(undefined)} />}
            </InfoDrawer>
        </>
    )
}

